// The route tree, defined in code rather than generated from files.
//
// A handful of routes do not justify a codegen step and its generated artifact
// in the repo — and §03 already accepts that this app forgoes the monorepo's codegen
// conventions where the two sides share types directly.
//
// `createAppRouter` is a FACTORY, not a module-level singleton, so a test can
// mount the real route tree on a memory history without inheriting another
// test's navigation state.

import {
  createRootRoute,
  createRoute,
  createRouter,
  redirect,
  type RouterHistory,
} from "@tanstack/react-router";
import { AppShell } from "./components/AppShell.js";
import { InvoiceSheetPage } from "./pages/invoices/InvoiceSheet.js";
import { InvoicesPage } from "./pages/invoices/InvoicesPage.js";
import { CalendarPage } from "./pages/calendar/CalendarPage.js";
import { MonthClosePage } from "./pages/close/MonthClosePage.js";
import { RunwayPage } from "./pages/runway/RunwayPage.js";
import { DashboardPage } from "./pages/DashboardPage.js";
import { FxRatesPage } from "./pages/fx/FxRatesPage.js";
import { RatesPage } from "./pages/rates/RatesPage.js";
import { DealDetailPage } from "./pages/deals/DealDetailPage.js";
import { DealsPage } from "./pages/deals/DealsPage.js";
import { FleetUsagePage } from "./pages/usage/FleetUsagePage.js";
import { UsageOverviewPage } from "./pages/usage/UsageOverviewPage.js";
import { UsageTrendPage } from "./pages/usage/UsageTrendPage.js";
import { NotFoundPage } from "./pages/NotFoundPage.js";
import { NotesPage } from "./pages/notes/NotesPage.js";
import { SearchPage } from "./pages/search/SearchPage.js";
import { PartnerDetailPage } from "./pages/partners/PartnerDetailPage.js";
import { PartnersPage } from "./pages/partners/PartnersPage.js";
import { MapPage } from "./pages/map/MapPage.js";
import { RegionsPage } from "./pages/regions/RegionsPage.js";
import { TrendsPage } from "./pages/trends/TrendsPage.js";
import { MarginPage } from "./pages/margin/MarginPage.js";
import { PricingPage } from "./pages/pricing/PricingPage.js";
import { ProjectionsPage } from "./pages/projections/ProjectionsPage.js";
import { validateCalendarSearch } from "./pages/calendar/calendarSearch.js";
import { validateCloseSearch } from "./pages/close/closeSearch.js";
import { validateDealsSearch } from "./pages/deals/dealsSearch.js";
import { validateMapSearch } from "./pages/map/mapSearch.js";
import { validateNotesSearch } from "./pages/notes/notesSearch.js";
import { validatePricingSearch } from "./pages/pricing/pricingSearch.js";
import { validateProjectionSearch } from "./pages/projections/projectionSearch.js";
import { SalesPage } from "./pages/sales/SalesPage.js";
import { validateSalesSearch } from "./pages/sales/salesSearch.js";
import { validateRegionsSearch } from "./pages/regions/regionsSearch.js";
import { validateSearchPageSearch } from "./pages/search/searchSearch.js";
import { validateTrendsSearch } from "./pages/trends/trendsSearch.js";
import { validateUsageSearch, validateUsageTrendSearch } from "./pages/usage/usageSearch.js";

const rootRoute = createRootRoute({
  component: AppShell,
  notFoundComponent: NotFoundPage,
});

const dashboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: DashboardPage,
});

const dealsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/deals",
  // The list's search, filters, sort and page live in the URL — see
  // `dealsSearch.ts` for why, and why this parse never throws.
  validateSearch: validateDealsSearch,
  component: DealsPage,
});

const dealDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/deals/$dealId",
  // `?note=` opens one note in the notes panel, which is how a search result
  // links to the note it found rather than to a list to scan again.
  validateSearch: validateNotesSearch,
  component: DealDetailPage,
});

/**
 * The cost explorer mounts UNDER the deal, because a person asks what a customer
 * costs and the deal is where the money already lives. Its controls — the month,
 * the breakdown, the deployment chips and the tenants — live in the URL, so
 * "look at Kingsport's September with nonprod on" is a link somebody can send.
 */
const dealUsageRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/deals/$dealId/usage",
  validateSearch: validateUsageSearch,
  component: UsageOverviewPage,
});

const dealUsageTrendRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/deals/$dealId/usage/trend",
  validateSearch: validateUsageTrendSearch,
  component: UsageTrendPage,
});

/**
 * The secondary entry, and the only cost screen with no deal above it: the facts
 * whose tenant no binding covers, which is exactly why it cannot hang off one.
 */
const fleetUsageRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/usage",
  validateSearch: validateUsageSearch,
  component: FleetUsagePage,
});

const invoicesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/invoices",
  component: InvoicesPage,
});

/**
 * One document. A route rather than a dialog over the list, because an invoice
 * being laid out is a place somebody comes back to across days — and a link to
 * "the Hansen September bill" is the thing they send to ask a question about it.
 */
const invoiceSheetRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/invoices/$invoiceId",
  component: InvoiceSheetPage,
});

/**
 * /billing is a redirect now, and no longer a screen.
 *
 * It used to be the schedule table: one row per bill somebody had generated in
 * advance. Nothing generates a bill any more — a period's charges are the
 * deal's term lines interpreted over that period — so the table has no rows to
 * draw. What a person came to /billing for is the month's work, and that is
 * /billing/close. The path stays because links to it were sent.
 */
const billingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/billing",
  beforeLoad: () => {
    throw redirect({ to: "/billing/close" });
  },
});

/**
 * Closing a month. The period lives in the URL. It is not a filter — it is
 * which month this IS — which is precisely why it cannot be component state:
 * "have a look at August" is the most ordinary sentence anybody says about a
 * close, and a bare /billing/close means a different month to everyone who
 * opens it. See `closeSearch.ts`, and why the default month is written as no
 * param at all.
 */
const monthCloseRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/billing/close",
  validateSearch: validateCloseSearch,
  component: MonthClosePage,
});

/**
 * The implementation runway. Not under /deals, because its whole argument is
 * that it shows more than one deal at a time — which is the question a deal page
 * structurally cannot answer.
 */
const runwayRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/implementation",
  component: RunwayPage,
});

const calendarRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/calendar",
  // The month, the kinds, the currency and the partner scope live in the URL —
  // see `calendarSearch.ts` for why, and why this parse never throws.
  validateSearch: validateCalendarSearch,
  component: CalendarPage,
});

const notesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/notes",
  // Which note is open lives in the URL, so a search result can link to the
  // note it found rather than to a list to scan again.
  validateSearch: validateNotesSearch,
  component: NotesPage,
});

const searchRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/search",
  validateSearch: validateSearchPageSearch,
  component: SearchPage,
});

const partnersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/partners",
  component: PartnersPage,
});

const partnerDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/partners/$partnerId",
  component: PartnerDetailPage,
});

/**
 * Revenue and incoming deals on a map, for presenting. Its controls live in the
 * URL — see `mapSearch.ts`.
 */
const mapRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/map",
  validateSearch: validateMapSearch,
  component: MapPage,
});

const regionsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/regions",
  // The grouping, the currency and the partner scope live in the URL for the
  // same reason the other screens' controls do — see `regionsSearch.ts`.
  validateSearch: validateRegionsSearch,
  component: RegionsPage,
});

/**
 * How the book of business has moved. Its own route rather than a tab on the
 * dashboard: the dashboard is about what is coming, and this is the one screen
 * that looks back. The window, the currency and the partner scope live in the
 * URL — see `trendsSearch.ts`.
 */
const trendsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/trends",
  validateSearch: validateTrendsSearch,
  component: TrendsPage,
});

/** A month's margin, customer by customer. The month is in the URL, as on the close. */
const marginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/margin",
  validateSearch: validateCloseSearch,
  component: MarginPage,
});

/**
 * What a proposed deal would make us. Every input is in the URL, which is how a
 * scenario is kept: nothing is saved anywhere else — see `pricingSearch.ts`.
 */
const pricingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/pricing",
  validateSearch: validatePricingSearch,
  component: PricingPage,
});

/**
 * Revenue run forward, with what is real kept apart from what is estimated.
 * The window, the scope and every scenario override live in the URL, so a
 * what-if is a link — see `projectionSearch.ts`.
 */
const projectionsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/projections",
  validateSearch: validateProjectionSearch,
  component: ProjectionsPage,
});

/**
 * The pipeline over time, from the deals' own estimates rather than invoices:
 * how it grew, what moved it, and which deals are going quiet. Its window and
 * measure live in the URL — see `salesSearch.ts`.
 */
const salesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/sales",
  validateSearch: validateSalesSearch,
  component: SalesPage,
});

const fxRatesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/fx-rates",
  component: FxRatesPage,
});

/**
 * The price rules. Top-level rather than nested under a deal, because a rate
 * prices every deal's facts: the card says what a unit costs, and the provider
 * cost facts beside it say what a provider told us a day actually cost.
 */
const ratesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/rates",
  component: RatesPage,
});

const routeTree = rootRoute.addChildren([
  dashboardRoute,
  dealsRoute,
  dealDetailRoute,
  dealUsageRoute,
  dealUsageTrendRoute,
  fleetUsageRoute,
  billingRoute,
  monthCloseRoute,
  runwayRoute,
  invoicesRoute,
  invoiceSheetRoute,
  calendarRoute,
  notesRoute,
  searchRoute,
  partnersRoute,
  partnerDetailRoute,
  mapRoute,
  regionsRoute,
  trendsRoute,
  marginRoute,
  pricingRoute,
  projectionsRoute,
  salesRoute,
  fxRatesRoute,
  ratesRoute,
]);

export function createAppRouter(history?: RouterHistory) {
  return createRouter({
    routeTree,
    // Real browser paths, not hashes: §04 has the API serving this SPA from its
    // own origin with an index.html fallback for unmatched GETs, so a hard
    // refresh on /deals/<id> resolves server-side.
    ...(history === undefined ? {} : { history }),
    defaultPreload: "intent",
  });
}

export type AppRouter = ReturnType<typeof createAppRouter>;

declare module "@tanstack/react-router" {
  interface Register {
    router: AppRouter;
  }
}
